/**
 * Subscription Tier Feature Limits
 *
 * Defines the feature limits and capabilities for each subscription tier.
 * This is the single source of truth for tier-based restrictions.
 */

export type SubscriptionTier = 'free' | 'pro';

export interface TierLimits {
  csvImport: {
    perMonth: number; // -1 = unlimited
    maxRowsPerImport: number; // -1 = unlimited
    aiColumnMapping: boolean;
    dataValidation: boolean;
    importHistory: boolean;
  };
  recurringPatterns: {
    perMonth: number; // -1 = unlimited
    maxActivePatterns: number; // -1 = unlimited
  };
  bulkOperations: {
    perMonth: number; // -1 = unlimited
    maxItemsPerOperation: number; // -1 = unlimited, 50 = Free tier limit
    previewEnabled: boolean;
    rollbackWindow: number; // hours (24 = Free, 48 = Pro)
  };
}

export const SUBSCRIPTION_LIMITS: Record<SubscriptionTier, TierLimits> = {
  free: {
    csvImport: {
      perMonth: -1,
      maxRowsPerImport: -1,
      aiColumnMapping: true,
      dataValidation: true,
      importHistory: true
    },
    recurringPatterns: {
      perMonth: 10,
      maxActivePatterns: 10
    },
    bulkOperations: {
      perMonth: 5,
      maxItemsPerOperation: 50,
      previewEnabled: true,
      rollbackWindow: 24
    }
  },

  pro: {
    csvImport: {
      perMonth: -1,
      maxRowsPerImport: -1,
      aiColumnMapping: true,
      dataValidation: true,
      importHistory: true
    },
    recurringPatterns: {
      perMonth: -1,
      maxActivePatterns: -1
    },
    bulkOperations: {
      perMonth: -1,
      maxItemsPerOperation: -1,
      previewEnabled: true,
      rollbackWindow: 48
    }
  }
};

/**
 * Get feature limits for a subscription tier
 */
export function getTierLimits(tier: SubscriptionTier): TierLimits {
  return SUBSCRIPTION_LIMITS[tier];
}

/**
 * Check if a feature is available for a tier
 */
export function hasFeature(
  tier: SubscriptionTier,
  feature: keyof TierLimits
): boolean {
  const limits = SUBSCRIPTION_LIMITS[tier];
  return limits[feature] !== undefined;
}

/**
 * Get upgrade benefits when moving from Free to Pro
 */
export function getUpgradeBenefits(): string[] {
  return [
    'Unlimited bulk operations (vs 5/month)',
    'Unlimited items per operation (vs 50 max)',
    'Unlimited recurring task executions (vs 10/month)',
    'Unlimited active recurring patterns (vs 10 max)',
    '48-hour rollback window (vs 24 hours)'
  ];
}

/**
 * Format limit value for display
 * -1 becomes "Unlimited", positive numbers stay as-is
 */
export function formatLimit(value: number): string {
  return value === -1 ? 'Unlimited' : value.toString();
}
